/**
 * ADR-0240 Agency Portal v2 Slice 7 — smart import parser.
 *
 * Takes raw paste/CSV/TSV/.xlsx text (or a parsed 2D grid) and
 * returns a validated, deduped list of `{domain, email?, company?,
 * contact_name?}` rows ready for the import preview + eventual
 * `POST /v1/agency/reports/bulk-import` body (§5).
 *
 * Pure, no I/O, no network, no DOM dependency. Runs in the agency's
 * own browser on their own file. The spec caps apply:
 *   - ≤ 50 will-scan rows per import (BULK_IMPORT_MAX_DOMAINS).
 *   - ≤ 5 000 rows read.
 *   - ≤ 50 columns.
 *
 * No new npm dependency: CSV parsing is RFC-4180 lite; .xlsx parsing
 * lives in `xlsx-lite.ts` (uses `DecompressionStream` + `DOMParser`,
 * both native).
 */

export type ImportRowReason =
  | 'duplicate_in_file'
  | 'invalid_domain'
  | 'free_email_domain'
  | 'empty'
  | 'scanned_recently'
  | 'over_batch_limit'
  | 'over_daily_limit';

export interface ParsedProspectRow {
  /** The original row index in the source (1-based for user-facing). */
  readonly row_index: number;
  /** Raw cell values from the source — kept for the "Input" column in
   *  the preview "Show rows" table. */
  readonly raw: string;
  readonly domain: string;
  readonly email: string | null;
  readonly company: string | null;
  readonly contact_name: string | null;
}

export interface SkippedProspectRow {
  readonly row_index: number;
  readonly raw: string;
  readonly reason: ImportRowReason;
  /** For `duplicate_in_file`: the earlier row this duplicates. */
  readonly duplicate_of?: number;
  /** For `scanned_recently`: the stored report URL. */
  readonly existing_report_url?: string | null;
}

export interface ColumnDetection {
  readonly domain: number | null;
  readonly email: number | null;
  readonly company: number | null;
  readonly contact_name: number | null;
  readonly header_row_detected: boolean;
}

export interface ParseResult {
  readonly rows: readonly ParsedProspectRow[];
  readonly skipped: readonly SkippedProspectRow[];
  readonly columns: ColumnDetection;
}

export const BULK_IMPORT_MAX_DOMAINS_CLIENT = 50;
export const PARSER_MAX_ROWS = 5000;
export const PARSER_MAX_COLS = 50;

/** Spec §5.2: domains whose email we never derive-and-scan. */
export const FREE_EMAIL_DOMAINS: ReadonlySet<string> = new Set([
  'gmail.com',
  'googlemail.com',
  'outlook.com',
  'hotmail.com',
  'live.com',
  'yahoo.com',
  'icloud.com',
  'me.com',
  'aol.com',
  'proton.me',
  'protonmail.com',
  'gmx.com',
  'gmx.net',
  'mail.com',
  'yandex.com',
  'yandex.ru',
  'zoho.com',
  'fastmail.com',
]);

/** Column header aliases (spec §5.1). Case-insensitive match. */
const HEADER_ALIASES = {
  domain: [
    'domain',
    'website',
    'site',
    'url',
    'web',
    'homepage',
    'company website',
    'company url',
  ],
  email: ['email', 'e-mail', 'email address', 'contact email', 'work email'],
  company: [
    'company',
    'company name',
    'organization',
    'organisation',
    'business',
    'account',
  ],
  contact_name: ['name', 'contact', 'contact name', 'full name', 'first name'],
} as const;

/** Shared hostname grammar — mirrors the server's `HOSTNAME_RE`. */
export const HOSTNAME_RE_CLIENT =
  /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)+$/;

/** Pragmatic email RE — same as server. */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export type NormalizedDomainClient =
  | { readonly ok: true; readonly domain: string }
  | { readonly ok: false; readonly reason: 'invalid_domain' | 'empty' };

/**
 * Normalize a raw domain / URL string. Mirrors the server's
 * `normalizeDomainInput` so an input that passes client-side also
 * passes server-side.
 */
export function normalizeDomainClient(raw: string): NormalizedDomainClient {
  let value = raw.trim();
  if (value.length === 0) return { ok: false, reason: 'empty' };
  // Strip surrounding quotes + <>.
  value = value.replace(/^[<"']+|[>"']+$/g, '').trim();
  if (value.length === 0) return { ok: false, reason: 'empty' };
  // Strip scheme.
  value = value.replace(/^https?:\/\//i, '');
  // Treat `user@host` in a domain cell as email → use host.
  const at = value.indexOf('@');
  if (at >= 0) value = value.slice(at + 1);
  // Strip path/query/fragment.
  value = value.split('/')[0] ?? '';
  value = value.split('?')[0] ?? '';
  value = value.split('#')[0] ?? '';
  // Strip port.
  value = value.replace(/:\d+$/, '');
  // Trailing dot.
  value = value.replace(/\.$/, '');
  // Leading www.
  value = value.replace(/^www\./i, '');
  value = value.toLowerCase();
  // IDN → punycode via URL API (no dependency).
  try {
    const u = new URL(`https://${value}`);
    value = u.hostname;
  } catch {
    return { ok: false, reason: 'invalid_domain' };
  }
  if (value.length === 0 || value.length > 253) return { ok: false, reason: 'invalid_domain' };
  if (!HOSTNAME_RE_CLIENT.test(value)) return { ok: false, reason: 'invalid_domain' };
  return { ok: true, domain: value };
}

/** Try to parse a row's cell as a header name. Returns the matching
 *  column field or null. */
function matchHeader(cell: string): keyof typeof HEADER_ALIASES | null {
  const lc = cell.trim().toLowerCase().replace(/\s+/g, ' ');
  for (const field of Object.keys(HEADER_ALIASES) as Array<keyof typeof HEADER_ALIASES>) {
    for (const alias of HEADER_ALIASES[field]) {
      if (lc === alias) return field;
    }
  }
  return null;
}

/** Detect the header row + column mapping. Returns per-field column
 *  indexes (-1 means "not found"). */
export function detectColumns(grid: readonly (readonly string[])[]): ColumnDetection {
  if (grid.length === 0) {
    return {
      domain: null,
      email: null,
      company: null,
      contact_name: null,
      header_row_detected: false,
    };
  }
  // Try header row: row 0 is a header if ≥ 1 cell matches an alias
  // AND no cell parses as a domain or email.
  const first = grid[0] as readonly string[];
  let header_matches = 0;
  let has_data_shape = false;
  const header_map: Record<string, number> = {};
  for (let i = 0; i < first.length; i += 1) {
    const cell = (first[i] ?? '').trim();
    if (cell.length === 0) continue;
    if (EMAIL_RE.test(cell)) {
      has_data_shape = true;
      continue;
    }
    const n = normalizeDomainClient(cell);
    if (n.ok) {
      has_data_shape = true;
      continue;
    }
    const field = matchHeader(cell);
    if (field !== null) {
      header_matches += 1;
      header_map[field] = i;
    }
  }
  if (header_matches >= 1 && !has_data_shape) {
    return {
      domain: header_map['domain'] ?? null,
      email: header_map['email'] ?? null,
      company: header_map['company'] ?? null,
      contact_name: header_map['contact_name'] ?? null,
      header_row_detected: true,
    };
  }
  // No header — infer per column. The column where > 50% of cells
  // parse as domain / URL is `domain`; the column with > 50% `@` is
  // `email`.
  const row_count = Math.min(grid.length, 200);
  const cols = Math.max(0, ...grid.slice(0, row_count).map((r) => r.length));
  let domain_col: number | null = null;
  let email_col: number | null = null;
  for (let c = 0; c < cols; c += 1) {
    let domain_hits = 0;
    let email_hits = 0;
    let total = 0;
    for (let r = 0; r < row_count; r += 1) {
      const cell = (grid[r]?.[c] ?? '').trim();
      if (cell.length === 0) continue;
      total += 1;
      if (EMAIL_RE.test(cell)) email_hits += 1;
      else if (normalizeDomainClient(cell).ok) domain_hits += 1;
    }
    if (total > 0 && domain_hits / total > 0.5 && domain_col === null) domain_col = c;
    if (total > 0 && email_hits / total > 0.5 && email_col === null) email_col = c;
  }
  return {
    domain: domain_col,
    email: email_col,
    company: null,
    contact_name: null,
    header_row_detected: false,
  };
}

export interface ParseGridOptions {
  readonly max_rows?: number;
  readonly explicit_columns?: Partial<ColumnDetection> | null;
}

/** Parse a 2D grid into typed prospect rows + skip reasons.
 *
 *   1. Detect columns (unless `explicit_columns` is supplied).
 *   2. For each data row:
 *      - trim/clean every cell;
 *      - derive the domain from the `domain` column, else from the
 *        `email` column (minus free-mail domains);
 *      - validate the domain via `normalizeDomainClient`;
 *      - dedupe within the file (first occurrence wins; later rows
 *        merge their missing contact fields into the first).
 */
export function parseProspectGrid(
  grid: readonly (readonly string[])[],
  options: ParseGridOptions = {},
): ParseResult {
  const columns = options.explicit_columns !== undefined && options.explicit_columns !== null
    ? {
        domain: options.explicit_columns.domain ?? null,
        email: options.explicit_columns.email ?? null,
        company: options.explicit_columns.company ?? null,
        contact_name: options.explicit_columns.contact_name ?? null,
        header_row_detected: options.explicit_columns.header_row_detected ?? false,
      }
    : detectColumns(grid);

  const start = columns.header_row_detected ? 1 : 0;
  const max_rows = Math.min(options.max_rows ?? PARSER_MAX_ROWS, PARSER_MAX_ROWS);
  const by_domain = new Map<string, ParsedProspectRow>();
  const rows: ParsedProspectRow[] = [];
  const skipped: SkippedProspectRow[] = [];

  const cell = (r: readonly string[], c: number | null): string =>
    c === null || c < 0 ? '' : (r[c] ?? '').trim();

  const end = Math.min(grid.length, start + max_rows);
  for (let i = start; i < end; i += 1) {
    const r = grid[i] as readonly string[];
    const raw_join = r.map((v) => v.trim()).join(' | ').slice(0, 240);
    const has_any = r.some((v) => v.trim().length > 0);
    if (!has_any) {
      skipped.push({ row_index: i + 1, raw: '', reason: 'empty' });
      continue;
    }

    const domain_cell = cell(r, columns.domain);
    const email_cell = cell(r, columns.email);
    const company_cell = cell(r, columns.company);
    const name_cell = cell(r, columns.contact_name);

    let candidate_domain: string | null = null;
    let derived_from_free_mail = false;

    if (domain_cell.length > 0) {
      const n = normalizeDomainClient(domain_cell);
      if (n.ok) candidate_domain = n.domain;
    }
    // If no domain column resolved + email exists, derive from email's
    // domain (skipping free-mail).
    if (candidate_domain === null && email_cell.length > 0) {
      const at = email_cell.lastIndexOf('@');
      if (at >= 0) {
        const host_raw = email_cell.slice(at + 1).trim().toLowerCase();
        if (FREE_EMAIL_DOMAINS.has(host_raw)) {
          derived_from_free_mail = true;
        } else {
          const n = normalizeDomainClient(host_raw);
          if (n.ok) candidate_domain = n.domain;
        }
      }
    }
    // Single-column source (paste) — try the first non-empty cell
    // when no column mapping exists.
    if (
      candidate_domain === null
      && columns.domain === null
      && columns.email === null
    ) {
      for (const c of r) {
        const t = c.trim();
        if (t.length === 0) continue;
        if (EMAIL_RE.test(t)) {
          const at = t.lastIndexOf('@');
          const host = t.slice(at + 1).toLowerCase();
          if (FREE_EMAIL_DOMAINS.has(host)) {
            derived_from_free_mail = true;
            break;
          }
          const n = normalizeDomainClient(host);
          if (n.ok) {
            candidate_domain = n.domain;
            break;
          }
        } else {
          const n = normalizeDomainClient(t);
          if (n.ok) {
            candidate_domain = n.domain;
            break;
          }
        }
      }
    }

    if (candidate_domain === null) {
      if (derived_from_free_mail) {
        skipped.push({
          row_index: i + 1,
          raw: raw_join,
          reason: 'free_email_domain',
        });
      } else {
        skipped.push({
          row_index: i + 1,
          raw: raw_join,
          reason: 'invalid_domain',
        });
      }
      continue;
    }

    // Dedupe within file — first wins; later rows donate missing
    // contact fields.
    const existing = by_domain.get(candidate_domain);
    if (existing !== undefined) {
      // Merge missing fields into the existing row.
      const email = existing.email ?? (EMAIL_RE.test(email_cell) ? email_cell.toLowerCase() : null);
      const company = existing.company ?? (company_cell.length > 0 && company_cell.length <= 80 ? company_cell : null);
      const contact_name = existing.contact_name ?? (name_cell.length > 0 && name_cell.length <= 80 ? name_cell : null);
      const merged: ParsedProspectRow = {
        row_index: existing.row_index,
        raw: existing.raw,
        domain: existing.domain,
        email,
        company,
        contact_name,
      };
      by_domain.set(candidate_domain, merged);
      // Replace in rows[].
      const idx = rows.findIndex((row) => row.domain === candidate_domain);
      if (idx >= 0) rows[idx] = merged;
      skipped.push({
        row_index: i + 1,
        raw: raw_join,
        reason: 'duplicate_in_file',
        duplicate_of: existing.row_index,
      });
      continue;
    }

    const email_v = EMAIL_RE.test(email_cell) ? email_cell.toLowerCase() : null;
    const company_v =
      company_cell.length > 0 && company_cell.length <= 80 ? company_cell : null;
    const name_v =
      name_cell.length > 0 && name_cell.length <= 80 ? name_cell : null;

    const parsed: ParsedProspectRow = {
      row_index: i + 1,
      raw: raw_join,
      domain: candidate_domain,
      email: email_v,
      company: company_v,
      contact_name: name_v,
    };
    rows.push(parsed);
    by_domain.set(candidate_domain, parsed);
  }

  // Enforce the 50 cap at the client — anything past that lands in
  // `skipped` with `over_batch_limit`.
  if (rows.length > BULK_IMPORT_MAX_DOMAINS_CLIENT) {
    const over = rows.slice(BULK_IMPORT_MAX_DOMAINS_CLIENT);
    rows.length = BULK_IMPORT_MAX_DOMAINS_CLIENT;
    for (const o of over) {
      skipped.push({
        row_index: o.row_index,
        raw: o.raw,
        reason: 'over_batch_limit',
      });
    }
  }

  return { rows, skipped, columns };
}

/** RFC-4180-lite CSV / TSV parser. No dependency.
 *
 * Handles:
 *   - quoted fields with `""` escapes;
 *   - CRLF or LF or CR line endings;
 *   - BOM strip at start of file;
 *   - delimiter sniff (`,`, `;`, `\t`) — the one with the highest
 *     average count across the first 10 lines wins.
 */
export function parseDelimited(
  raw: string,
  delimiter?: ',' | ';' | '\t',
): string[][] {
  let text = raw.startsWith('﻿') ? raw.slice(1) : raw;
  const delim = delimiter ?? sniffDelimiter(text);
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let in_quotes = false;
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i] as string;
    if (in_quotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i += 1;
        } else {
          in_quotes = false;
        }
      } else {
        field += ch;
      }
      continue;
    }
    if (ch === '"') {
      in_quotes = true;
      continue;
    }
    if (ch === delim) {
      row.push(field);
      field = '';
      continue;
    }
    if (ch === '\r' || ch === '\n') {
      // Fold CRLF into one break.
      if (ch === '\r' && text[i + 1] === '\n') i += 1;
      row.push(field);
      // Skip empty final rows.
      if (row.length > 1 || row[0] !== '') rows.push(row);
      row = [];
      field = '';
      continue;
    }
    field += ch;
  }
  if (field.length > 0 || row.length > 0) {
    row.push(field);
    if (row.length > 1 || row[0] !== '') rows.push(row);
  }
  // Clamp cols
  if (rows.length > PARSER_MAX_ROWS) rows.length = PARSER_MAX_ROWS;
  for (const r of rows) if (r.length > PARSER_MAX_COLS) r.length = PARSER_MAX_COLS;
  return rows;
}

function sniffDelimiter(text: string): ',' | ';' | '\t' {
  const lines = text.split(/\r?\n/).slice(0, 10);
  const counts = { ',': 0, ';': 0, '\t': 0 } as const;
  const totals: Record<',' | ';' | '\t', number> = { ',': 0, ';': 0, '\t': 0 };
  for (const l of lines) {
    for (const k of Object.keys(counts) as Array<keyof typeof counts>) {
      totals[k] += (l.match(new RegExp(k === '\t' ? '\\t' : `\\${k}`, 'g')) ?? []).length;
    }
  }
  const best = (Object.entries(totals) as Array<[',' | ';' | '\t', number]>).reduce(
    (a, b) => (b[1] > a[1] ? b : a),
    [',', 0] as [',' | ';' | '\t', number],
  );
  return best[1] > 0 ? best[0] : ',';
}

/** Parse a plain paste (one domain per line, or email-only, or a
 *  mixed single column). Reuses `parseDelimited` with newline-only. */
export function parsePaste(raw: string): string[][] {
  const text = raw.startsWith('﻿') ? raw.slice(1) : raw;
  const out: string[][] = [];
  for (const line of text.split(/\r?\n|\r/)) {
    out.push([line.trim()]);
    if (out.length >= PARSER_MAX_ROWS) break;
  }
  return out;
}
